import { useState } from 'react';
import { AdminCompanySettingsPageProps } from '.';
import api from '../../../../api';
import AlertBox from '../../../components/AlertBox';
import Button from '../../../components/Button';
import { DeprecatedButtonContainer } from '../../../components/ButtonContainer';
import PageStaticSection from '../../../components/PageStaticSection';
import SaveButton, { ENABLED, SAVED, SAVING } from '../../../components/SaveButton';
import { redirect } from '../../../services/Router';
import AdvancedSettings from './inputs/AdvancedSettings';
import CompanySettings from './inputs/CompanySettings';
import AdminDateTime from './inputs/DateTime';
import DefaultObjectiveCycleSettings from './inputs/DefaultObjectiveCycleSettings';
import MoreAdvancedSettings from './inputs/MoreAdvancedSettings';
import {
    CompanySettingsAdvancedModel,
    create,
    getButtonState,
    getCompanySettingsAdvanced,
    getDateTimeSettings,
    setButtonState,
} from './model';
import styles from './style.m.less';

export type ModelProps = {
    model: CompanySettingsAdvancedModel;
    setModel: (model: CompanySettingsAdvancedModel) => void;
};

const AdminCompanySettings = ({ companySettingsAdvanced, dateTimeSettings }: AdminCompanySettingsPageProps) => {
    const [model, setModel] = useState(create(companySettingsAdvanced, dateTimeSettings, ENABLED));

    // Store initial values for comparison
    const initialCompanySettings = companySettingsAdvanced;
    const initialDateTimeSettings = dateTimeSettings;

    const handleSubmit = async (
        actionModel: CompanySettingsAdvancedModel,
        updateModel: (model: CompanySettingsAdvancedModel) => void
    ) => {
        updateModel(setButtonState(SAVING, actionModel));

        const companySettingsChanged =
            JSON.stringify(getCompanySettingsAdvanced(actionModel)) !== JSON.stringify(initialCompanySettings);
        const dateTimeSettingsChanged =
            JSON.stringify(getDateTimeSettings(actionModel)) !== JSON.stringify(initialDateTimeSettings);

        if (companySettingsChanged) {
            await api.companySettingsAdvanced.setCompanySettingsAdvanced(getCompanySettingsAdvanced(actionModel));
        }

        if (dateTimeSettingsChanged) {
            await api.companyDateTimeSettings.setDateTimeSettings(getDateTimeSettings(actionModel));
        }

        updateModel(setButtonState(SAVED, model));
        setTimeout(() => updateModel(setButtonState(ENABLED, model)), 4000);
    };

    const Actions = ({ model: actionModel, setModel: updateModel }: ModelProps) => {
        const saveProps = {
            onClick: () => handleSubmit(actionModel, updateModel),
            state: getButtonState(actionModel),
        };

        const cancelProps = {
            onClick: () => redirect('/app/admin/settings'),
            children: 'Cancel',
        };
        return (
            <DeprecatedButtonContainer className={styles.buttons}>
                <Button {...cancelProps} />
                <SaveButton {...saveProps} />
            </DeprecatedButtonContainer>
        );
    };

    return (
        <>
            <PageStaticSection>
                {getButtonState(model) === 'SAVED' && (
                    <AlertBox colour="YELLOW">Your changes have been saved.</AlertBox>
                )}
                <CompanySettings model={model} setModel={setModel} />
                <AdminDateTime model={model} setModel={setModel} />
                <AdvancedSettings model={model} setModel={setModel} />
                <Actions model={model} setModel={setModel} />
            </PageStaticSection>

            <PageStaticSection>
                <DefaultObjectiveCycleSettings />
                <MoreAdvancedSettings />
            </PageStaticSection>
        </>
    );
};

export default AdminCompanySettings;
